import { useMemo } from 'react';

import dayjs from 'dayjs';
import round from 'lodash/round';

import { DATE_SIMPLE } from '@cast/constants';
import {
  Column,
  DataTable,
  RatioProgressBar,
  TableHeader,
  Typography,
  Stack,
} from '@cast/design-system';
import { convertUnit } from '@cast/utils';

import { HourlyConsumedResources } from 'features/cost-report/components/HourlyConsumedResources';
import {
  NodePricingInfo,
  ValueRenderType,
} from 'features/cost-report/components/NodePricingInfo';
import { formatDailyMetricsCost } from 'features/cost-report/utils/metrics/formatDailyMetricsCost';

import { formatMinutesToHours } from '../../_utils';
import { NormalizedWorkloadGpuUtilizationSummaryInterval } from '../_types';

import { NoData } from '@cast/shared-search-ui';

type WorkloadGpuUtilizationTableProps = {
  intervals: NormalizedWorkloadGpuUtilizationSummaryInterval[];
  isLoading: boolean;
  timezone: string;
  cacheKey: string;
  urlKey?: string;
  testId: string;
  showGpuMetrics: boolean;
};

export const WorkloadGpuUtilizationTable = ({
  intervals,
  isLoading,
  timezone,
  cacheKey,
  urlKey,
  testId,
  showGpuMetrics,
}: WorkloadGpuUtilizationTableProps) => {
  const sortedData = useMemo(() => {
    return intervals.sort((a, b) =>
      dayjs(a.timestamp).isBefore(b.timestamp) ? 1 : -1
    );
  }, [intervals]);

  if (!intervals.length && !isLoading) {
    return <NoData />;
  }

  return (
    <DataTable<NormalizedWorkloadGpuUtilizationSummaryInterval>
      cacheKey={cacheKey}
      urlKey={urlKey}
      testId={testId}
      maxHeight="80vh"
      outerHeader={<TableHeader recordsCount="Daily GPU spend details" />}
      isLoading={isLoading}
      data={sortedData}
    >
      <Column<NormalizedWorkloadGpuUtilizationSummaryInterval>
        id="timestamp"
        minWidth={224}
        header="date"
        renderCell={({ timestamp }) =>
          dayjs.utc(timestamp).tz(timezone).format(DATE_SIMPLE)
        }
        resizable
        sortable
      />
      <Column id="podCount" minWidth={88} header="Pods" sortable />
      <Column<NormalizedWorkloadGpuUtilizationSummaryInterval>
        id="idleMinutes"
        minWidth={108}
        header="idle time"
        sortable
        renderCell={({ idleMinutes }) =>
          `${formatMinutesToHours(idleMinutes)} h`
        }
      />
      <Column
        id="avgGpuUtil"
        minWidth={171}
        header="avg. GPU utilization"
        renderCell={({
          utilization,
        }: NormalizedWorkloadGpuUtilizationSummaryInterval) => {
          const usage = round(utilization?.gpuPercent || 0, 2);
          return (
            <Stack gap={1}>
              <Typography variant="P12B">{usage}%</Typography>
              <RatioProgressBar
                hideBorders
                size="md"
                sx={{
                  flexGrow: 1,
                }}
                total={100}
                bars={[
                  {
                    value: usage,
                    color: 'aqua.600',
                  },
                ]}
              />
            </Stack>
          );
        }}
        sortable
        accessor="utilization.gpuPercent"
        sortingType="number"
      />
      <Column<NormalizedWorkloadGpuUtilizationSummaryInterval>
        infoTooltip="GPU memory that was available but not allocated or utilized by the workload during the specified time period."
        id="avgWastedMem"
        minWidth={171}
        header="avg. wasted mem."
        renderCell={({ memory }) => {
          return (
            <HourlyConsumedResources
              requests={convertUnit(
                memory?.availableBytes || 0,
                'Bytes',
                'GiB'
              )}
              usage={convertUnit(memory?.usedBytes || 0, 'Bytes', 'GiB')}
              wasted={convertUnit(memory?.wastedBytes || 0, 'Bytes', 'GiB')}
              label="AVERAGE HOURLY WASTED GPU MEMORY"
              unit="GiB"
            />
          );
        }}
        sortable
        accessor="memory.wastedBytes"
        sortingType="number"
      />
      {showGpuMetrics && (
        <Column<NormalizedWorkloadGpuUtilizationSummaryInterval>
          id="wasted"
          minWidth={171}
          header="$ wasted"
          infoTooltip="This represents wasted GPU expenses, directly resulting from GPU idle time. It's calculated by multiplying the idle time by the cost of the GPU."
          renderCell={({
            costImpact,
          }: NormalizedWorkloadGpuUtilizationSummaryInterval) => {
            if (!costImpact) {
              return <span data-sortweight="0">-</span>;
            }

            return (
              <Stack width="100%" direction="column" gap={1}>
                <Typography
                  variant="inherit"
                  fontWeight={600}
                  data-sortweight={costImpact.total}
                >
                  {formatDailyMetricsCost(costImpact.total)}
                </Typography>
                <NodePricingInfo
                  hideTotal
                  fullWidth
                  type={ValueRenderType.Money}
                  onDemand={costImpact?.onDemand}
                  fallback={costImpact?.spotFallback}
                  spot={costImpact?.spot}
                  progressBarSx={{ height: '4px' }}
                  sx={{ lineHeight: '4px' }}
                />
              </Stack>
            );
          }}
          sortable
          accessor={(row) => row.costImpact?.total ?? 0}
        />
      )}
    </DataTable>
  );
};
